import { ReactElement, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { BreakpointMedia } from '../../utils/config';
import {
  defaultFilterTags,
  getFilterDisplayMap,
  SelectedFilter,
  TopLevelFilters,
} from '../../constants/menu';
import { FilterDropdown } from './FilterDropdown';
import styles from './MenuPage.module.scss';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { getFormattedDeliveryDateRange } from '../../utils/dateTimes';
import { useAuth } from '../../contexts/AuthContext';
import { SignupStep } from '../../constants/signup';
import { formatDeliveryDate } from '../../hooks/useFormattedDate';
import { useLoggedInMenuIdQuery } from '../../services/menu/menu.queries';

type Props = {
  triggerLoadingShimmer: () => void;
  triggerTileShiftAnimation: () => void;
}

/**
 * @param selectedFilters - currently selected filters before user clicks a new filter
 * @param setSelectedFilters - saves the newly selected filters in the top-level container state
 * @param filter - top level or second level filter string being clicked
 * @param triggerSwitchFilterAnimation - triggers the loading shimmer display
 */
const switchFilters = (
  selectedFilters: SelectedFilter,
  setSelectedFilters: (selectedFilters: SelectedFilter, userInitiated: boolean) => void,
  filter: string,
  triggerSwitchFilterAnimation?: () => void,
) => {
  // eslint-disable-next-line camelcase
  const menu_filtering_multi_select = true; // TODO: change to featureFlag
  if (filter === Object.keys(selectedFilters)[0]) {
    return; // do nothing if it's the existing top level filter
  }

  // Scroll to top on new filter
  window.scrollTo(0, 0);

  // trigger the loading shimmer while filter is being switched
  if (triggerSwitchFilterAnimation) {
    triggerSwitchFilterAnimation();
  }

  let topLevelFilter = TopLevelFilters.ViewAll; // default to all
  let secondLevelFilter = ['All']; // default to all

  // change to new top level filter or use the existing otherwise
  topLevelFilter = Object.keys(defaultFilterTags)
    .includes(filter) ? TopLevelFilters[filter] : TopLevelFilters[Object.keys(selectedFilters)[0]];

  // need to remove sub-filter when double-clicked
  const subFilterAlreadySelected = Object.values(selectedFilters)[0].includes(filter);
  let subFiltersFilterToggledOff: string[];
  // eslint-disable-next-line camelcase
  if (menu_filtering_multi_select) {
    subFiltersFilterToggledOff = Object.values(selectedFilters)[0]
      .filter((newFilter) => newFilter !== filter);
  } else {
    subFiltersFilterToggledOff = ['All'];
  }

  // subfilters already contain `filter`? then newSubFilters =
  // yes: collection of all selected filters minus `filter` arg
  // no: collection of all selected filters pre-pend `filter` arg
  let newSubFilters: string[];
  // eslint-disable-next-line camelcase
  if (menu_filtering_multi_select) {
    newSubFilters = subFilterAlreadySelected
      ? subFiltersFilterToggledOff
      : [filter, ...Object.values(selectedFilters)[0]];
  } else {
    newSubFilters = subFilterAlreadySelected
      ? subFiltersFilterToggledOff
      : [filter];
  }

  // remove 'All' from sub-filters if anything else is selected
  newSubFilters = newSubFilters.filter((filt) => filt !== 'All');

  // if we're changing a top level filter, but it's not the current, change to "all"
  // otherwise, add new filter to existing selectedFilters array
  secondLevelFilter = Object.keys(defaultFilterTags)
    .includes(filter) ? ['All'] : newSubFilters;
  if (filter === 'All' || secondLevelFilter.length === 0) {
    secondLevelFilter = ['All'];
  }

  setSelectedFilters({
    [TopLevelFilters[topLevelFilter]]: secondLevelFilter,
  }, true);
};

const green = '#297B03';
const white = '#F8F8F8';
const black = '#000000';

export function FilterHeader({
  triggerLoadingShimmer,
  triggerTileShiftAnimation,
}: Props): ReactElement {
  const isNarrowScreen = useMediaQuery({ query: BreakpointMedia.MAX_LG });
  const {
    menu,
    selectedFilters,
    setSelectedFilters,
    selectedDropdownFilters,
    setSelectedDropdownFilters,
    filteredResultCount,
  } = useMenuWithCart();
  const { isLoggedIn } = useAuth();
  const shippingOnDate = menu?.deliveryOn ?? '';
  const deliveryDateRange = getFormattedDeliveryDateRange(shippingOnDate, false);
  const { currentSignupStep } = useMenuWithCart();
  const isPaying = currentSignupStep === SignupStep.PAYMENT;
  const menuId = menu?.id;
  const loggedinMenu = useLoggedInMenuIdQuery(menuId, isPaying).data;
  const deliveryOn = isPaying ? loggedinMenu?.deliveryOn : menu?.deliveryOn;
  const deliveryDateFormatted = useMemo(() => formatDeliveryDate(deliveryOn), [deliveryOn]);
  const dateSource = isPaying ? deliveryDateFormatted : deliveryDateRange;
  const filterDisplayMap = isLoggedIn ? getFilterDisplayMap()
    : getFilterDisplayMap(dateSource);
  return (
    <>
      <div className={styles.filter_header_container}>
        <div className={styles.top_level_filters}>
          <div role="group" aria-label="Meal category" style={{ display: 'flex' }}>
            {!isNarrowScreen && <div className={styles.vertical_line} />}
            {isNarrowScreen && (
              <div style={{ display: 'flex' }}>
                <FilterDropdown
                  selectedDropdownFilters={selectedDropdownFilters}
                  setSelectedDropdownFilters={setSelectedDropdownFilters}
                  filteredResultCount={filteredResultCount}
                  triggerTileShiftAnimation={triggerTileShiftAnimation}
                />
                <div className={styles.vertical_line} />
              </div>
            )}
            {Object.values(TopLevelFilters)
              .map((filter) => {
                const isSelected = filter === Object.keys(selectedFilters)[0];
                const backgroundColor = isSelected ? green : white;
                const textColor = isSelected ? white : black;
                const FilterIcon = filterDisplayMap[TopLevelFilters[filter]].icon;

                return (
                  <button
                    key={filter}
                    className={`${styles.filter_button} ${styles.top_level}`}
                    aria-pressed={isSelected}
                    style={{
                      backgroundColor,
                      color: textColor,
                      border: 'none',
                    }}
                    onClick={
                      () => switchFilters(selectedFilters, setSelectedFilters, filter,
                        triggerLoadingShimmer)
                    }
                  >
                    <FilterIcon
                      fill={textColor}
                      width="16px"
                      height="16px"
                      style={{
                        marginRight: '4px',
                      }}
                    />
                    {' '}
                    {filterDisplayMap[filter].displayName}
                  </button>
                );
              })}
          </div>
          {!isNarrowScreen && (
            <FilterDropdown
              selectedDropdownFilters={selectedDropdownFilters}
              setSelectedDropdownFilters={setSelectedDropdownFilters}
              filteredResultCount={filteredResultCount}
              triggerTileShiftAnimation={triggerTileShiftAnimation}
            />
          )}
        </div>
        <div
          id="second-level-filter"
          role="group"
          aria-label="Dietary filters"
          className={styles.second_level_filters}
        >
          {defaultFilterTags[Object.keys(selectedFilters)[0]].map((filter: string) => {
            const isSelected = Object.values(selectedFilters)[0].includes(filter);
            const filterColor = isSelected ? green : white;
            const textColor = isSelected ? white : green;
            return (
              <button
                key={filter}
                className={`${styles.filter_button} ${styles.second_level}`}
                aria-pressed={isSelected}
                style={{
                  backgroundColor: filterColor,
                  color: textColor,
                  border: 'none',
                }}
                onClick={
                  // switchFilters called without shimmer trigger for filter tags
                  () => switchFilters(selectedFilters,
                    setSelectedFilters, filter, triggerTileShiftAnimation)
                }
              >
                {filter}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
